import { useState, useMemo } from 'react'
import type { StrategyNode } from '../../../types/BaseNode.tsx'
import { StrategyNodeCard } from './componentCards/StrategyNodeCard.tsx'
import { replaceNodeInTree, deleteNodeInTree, wrapNodeInComposite, wrapNodeInFormatter } from '../../../utils/treeHandler'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { findNodeById } from '../../../utils/treeHandler';
import { promoteNodeToParent } from '../../../utils/treeHandler';
import { handleNodeDrop } from '../../../utils/handleDrop.ts'
import type { ErrorNode } from '../../../types/ErrorNode.ts'
import { CompositeNode } from '../../../types/CompositeNode.ts';
import { FormatterNode } from '../../../types/FormatterNode.ts';
import { SingleNode } from '../../../types/SingleNode.ts';
import { PnLNode } from '../../../types/PnLNode.ts';
import { VarianceNode } from '../../../types/VarianceNode.ts';
import { MeepMeepNode } from '../../../types/MeepMeepNode.ts';
import { v4 as uuid } from 'uuid';
import { UndoHandler } from '../../../utils/undoHandler.ts';
import { deserializeNode, serializeNode } from '../../../utils/serializeNode';
import styles from './Canvas.module.css';
import { NodeOperationsContext } from '../../../contexts/NodeOperationsContext';
import { calculateLayout, flattenTree, calculateBounds, type ActualDimensionsMap } from '../../../utils/treeLayout';
import { TreeConnectors } from './TreeConnectors';
import { useOperators } from '../../../hooks/useOperators';
import { getDefaultOperatorStatic } from '../../../hooks/useOperators';
import { getDefaultCombineOnStatic } from '../../../hooks/useCombineOptions';
import { PlaceholderCard } from './placeholder/PlaceholderCard';
import type { StrategyFamily } from '../../../types/Strategy';

const CANVAS_PADDING = 120; // px padding around the tree bounding box

type StarterAction = {
    id: string;
    label: string;
    borderColor?: string;
    create: () => StrategyNode;
};

function getStarterActions(strategyFamily: StrategyFamily): StarterAction[] {
    const sharedBuild: StarterAction[] = [
        { id: 'Single', label: '+ Data', create: () => new SingleNode(uuid(), "Data") },
        { id: 'Formatter', label: '+ Transform', create: () => new FormatterNode(uuid(), "Transform") },
    ];

    if (strategyFamily === 'meepmeep') {
        return [
            {
                id: 'MeepMeep',
                label: '+ MeepMeep',
                borderColor: 'var(--meep-cyan)',
                create: () => new MeepMeepNode(uuid(), "MeepMeep Strategy"),
            },
        ];
    }

    if (strategyFamily === 'quantarc') {
        return [
            ...sharedBuild,
            { id: 'Composite', label: '+ Combine', create: () => new CompositeNode(uuid(), "Combine", getDefaultOperatorStatic(), getDefaultCombineOnStatic()) },
            { id: 'PnL', label: '+ PnL', borderColor: 'var(--nature-result)', create: () => new PnLNode(uuid(), "PnL") },
        ];
    }

    return [
        { id: 'Single', label: '+ Data', create: () => new SingleNode(uuid(), "Data") },
        { id: 'Composite', label: '+ Combine', create: () => new CompositeNode(uuid(), "Combine", getDefaultOperatorStatic(), getDefaultCombineOnStatic()) },
        { id: 'Formatter', label: '+ Transform', create: () => new FormatterNode(uuid(), "Transform") },
        { id: 'PnL', label: '+ PnL', borderColor: 'var(--nature-result)', create: () => new PnLNode(uuid(), "PnL") },
        { id: 'Variance', label: '+ Variance', borderColor: 'var(--accent-warning)', create: () => new VarianceNode(uuid(), "Variance") },
    ];
}

interface Props {
    nodes: StrategyNode[];
    setNodes: React.Dispatch<React.SetStateAction<StrategyNode[]>>;
    onQuickEvaluate: () => void;
    isEvaluating: boolean;
    onSubtreeEvaluate: (node: StrategyNode) => void;
    onCreateStrategyFromNode: (node: StrategyNode) => void;
    start: string | null;
    end: string | null;
    setStart: React.Dispatch<React.SetStateAction<string | null>>;
    setEnd: React.Dispatch<React.SetStateAction<string | null>>;
    onStrategySaved: () => void;
    errorNode: ErrorNode | null;
    undoHandler: UndoHandler;
    nodeMetadata?: Record<string, unknown>;
    onNodeMetadataUpdate: (metadata?: Record<string, unknown>) => void;
    onPreviewResultUpdate: (response: unknown, nodeId: string) => void;
    isLocked?: boolean;
    strategyFamily: StrategyFamily;
}

export function Canvas({ nodes, setNodes, onQuickEvaluate, isEvaluating, onSubtreeEvaluate, onCreateStrategyFromNode, start, end, setStart, setEnd, onStrategySaved, errorNode, undoHandler, nodeMetadata, onNodeMetadataUpdate, onPreviewResultUpdate, isLocked = false, strategyFamily }: Props) {
    // ── Collapse ─────────────────────────────────────────────────
    const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());
    const [actualDimensions, setActualDimensions] = useState<ActualDimensionsMap>(new Map());

    const [isInteracting] = useState(false);
    const [skipUndoPush, setSkipUndoPush] = useState(false);
    const [elevatedId, setElevatedId] = useState<string | null>(null);
    const starterActions = useMemo(() => getStarterActions(strategyFamily), [strategyFamily]);

    const toggleCollapsed = (id: string) => {
        setCollapsedNodes(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const isCollapsed = (id: string) => collapsedNodes.has(id);

    // ── Layout Engine ────────────────────────────────────────────
    const layoutMap = useMemo(() => {
        if (nodes.length === 0) return new Map();
        return calculateLayout(nodes[0], isCollapsed, actualDimensions);
    }, [nodes, collapsedNodes, actualDimensions]);

    // Build a quick lookup for parent operators to help children style themselves
    const { getOperatorFlags } = useOperators();
    const parentOperatorMap = useMemo(() => {
        const map = new Map<string, string>();
        const traverse = (n: StrategyNode) => {
            if (n.type === 'Composite') {
                const op = (n.config as any).operator;
                if (op) {
                    if (n.config.left) { map.set(n.config.left.id, op); traverse(n.config.left); }
                    else { map.set(`placeholder-${n.id}-left`, op); }
                    if (n.config.right) { map.set(n.config.right.id, op); traverse(n.config.right); }
                    else { map.set(`placeholder-${n.id}-right`, op); }
                }
            } else if (n.type === 'Formatter') {
                const typeId = (n.config as any).typeId || "Transform";
                if (n.config.child) {
                    map.set(n.config.child.id, typeId);
                    traverse(n.config.child);
                } else {
                    map.set(`placeholder-${n.id}-child`, typeId);
                }
            } else if (n.type === 'PnL') {
                const label = "PnL";
                if (n.config.entry) { map.set(n.config.entry.id, label); traverse(n.config.entry); }
                else { map.set(`placeholder-${n.id}-entry`, label); }
                if (n.config.exit) { map.set(n.config.exit.id, label); traverse(n.config.exit); }
                else { map.set(`placeholder-${n.id}-exit`, label); }
            } else if (n.type === 'Variance') {
                const label = "Variance";
                if (n.config.target) { map.set(n.config.target.id, label); traverse(n.config.target); }
                else { map.set(`placeholder-${n.id}-target`, label); }
                (n.config.parameters ?? []).forEach((p: StrategyNode) => {
                    map.set(p.id, label); traverse(p);
                });
                map.set(`placeholder-${n.id}-param-${(n.config.parameters ?? []).length}`, label);
            }
        };
        if (nodes[0]) traverse(nodes[0]);
        return map;
    }, [nodes]);

    const parentTypeMap = useMemo(() => {
        const map = new Map<string, StrategyNode['type']>();
        const traverse = (n: StrategyNode) => {
            if (n.type === 'Composite') {
                if (n.config.left) { map.set(n.config.left.id, n.type); traverse(n.config.left); }
                if (n.config.right) { map.set(n.config.right.id, n.type); traverse(n.config.right); }
            } else if (n.type === 'Formatter') {
                if (n.config.child) {
                    map.set(n.config.child.id, n.type);
                    traverse(n.config.child);
                }
            } else if (n.type === 'PnL') {
                if (n.config.entry) { map.set(n.config.entry.id, n.type); traverse(n.config.entry); }
                if (n.config.exit) { map.set(n.config.exit.id, n.type); traverse(n.config.exit); }
            } else if (n.type === 'Variance') {
                if (n.config.target) { map.set(n.config.target.id, n.type); traverse(n.config.target); }
                (n.config.parameters ?? []).forEach((p: StrategyNode) => {
                    map.set(p.id, n.type);
                    traverse(p);
                });
            } else if (n.type === 'MeepMeep') {
                if (n.config.main_market) { map.set(n.config.main_market.id, n.type); traverse(n.config.main_market); }
                if (n.config.signal) { map.set(n.config.signal.id, n.type); traverse(n.config.signal); }
            } else if (n.type === 'Regression') {
                (n.config.inputs ?? []).forEach((p: StrategyNode) => {
                    map.set(p.id, n.type);
                    traverse(p);
                });
            }
        };
        if (nodes[0]) traverse(nodes[0]);
        return map;
    }, [nodes]);

    // Compute which nodes are conditional parents (Filter/Comparison) using the reactive hook
    // This avoids the timing issue of reading the cache outside of React
    const logicParentSets = useMemo(() => {
        const filters = new Set<string>();
        const comparisons = new Set<string>();
        for (const layout of layoutMap.values()) {
            if (!layout.parentId) continue;
            const op = parentOperatorMap.get(layout.id) ?? parentOperatorMap.get(layout.parentId) ?? "";

            const { isFilter, isComparison } = getOperatorFlags(op);
            if (isFilter) filters.add(layout.parentId);
            if (isComparison) comparisons.add(layout.parentId);
        }
        return { filters, comparisons };
    }, [layoutMap, parentOperatorMap, getOperatorFlags]);

    const flatNodes = useMemo(() => {
        if (nodes.length === 0) return [];
        return flattenTree(nodes[0], isCollapsed);
    }, [nodes, collapsedNodes]);

    const bounds = useMemo(() => calculateBounds(layoutMap), [layoutMap]);

    const canvasWidth = Math.max(3000, bounds.width + CANVAS_PADDING * 2);
    const canvasHeight = Math.max(2000, bounds.height + CANVAS_PADDING * 2);

    // ── Tree mutations ────────────────────────────────────────────
    const handleUpdate = (id: string, patch: StrategyNode) => {
        handleUpdateNodes(nodes.map(n => replaceNodeInTree(n, id, patch)!));
    };

    const handleDelete = (id: string) => {
        handleUpdateNodes(nodes.map(n => deleteNodeInTree(n, id)).filter(Boolean) as StrategyNode[]);
    };

    const handleWrapInComposite = (id: string) => {
        handleUpdateNodes(nodes.map(n => wrapNodeInComposite(n, id)!));
    };

    const handleWrapInFormatter = (id: string) => {
        handleUpdateNodes(nodes.map(n => wrapNodeInFormatter(n, id)!));
    };

    function handleInsertRootNode(newNode: StrategyNode) {
        if (nodes.length === 0) {
            if (strategyFamily === 'meepmeep' && newNode.type !== 'MeepMeep') {
                window.alert('MeepMeep mode must start with a MeepMeep root node.');
                return;
            }
            handleUpdateNodes([newNode]);
        }
    }

    const handlePromoteToParent = (id: string) => {
        handleUpdateNodes(nodes.map(n => promoteNodeToParent(n, id)!));
    };

    const handleUpdateNodes = (newNodes: StrategyNode[]) => {
        if (!undoHandler) { return; }
        if (!skipUndoPush && nodes.length > 0) {
            undoHandler.push(structuredClone(nodes[0]));
        }
        setNodes(newNodes);
    };

    const handleMakeRoot = (id: string) => {
        const node = findNodeById(nodes[0], id);
        if (node) {
            undoHandler.push(structuredClone(nodes[0]));
            setNodes([node]);
        }
    };

    const handleUndo = () => {
        setSkipUndoPush(true);
        const previous = undoHandler.undo(nodes[0]);
        if (previous) {
            setNodes([previous]);
            setTimeout(() => setSkipUndoPush(false), 0);
        } else {
            setSkipUndoPush(false);
        }
    };

    const handleRedo = () => {
        setSkipUndoPush(true);
        const next = undoHandler.redo(nodes[0]);
        if (next) {
            setNodes([next]);
            setTimeout(() => setSkipUndoPush(false), 0);
        } else {
            setSkipUndoPush(false);
        }
    };

    const nodeOps = {
        onUpdate: handleUpdate,
        onDelete: handleDelete,
        onWrapInComposite: handleWrapInComposite,
        onWrapInFormatter: handleWrapInFormatter,
        onCalculate: onSubtreeEvaluate,
        onMakeRoot: handleMakeRoot,
        onPromoteToParent: handlePromoteToParent,
        onStrategySaved,
        toggleCollapsed,
        isCollapsed,
        errorNode,
        onCreateStrategyFromNode,
        rootNode: nodes[0] || null,
        reportDimensions: (id: string, width: number, height: number) => {
            setActualDimensions((prev: ActualDimensionsMap) => {
                const existing = prev.get(id);
                if (existing?.width === width && existing?.height === height) return prev;
                const next = new Map(prev);
                next.set(id, { width, height });
                return next;
            });
        },
        updateNodeMetadata: onNodeMetadataUpdate,
        updatePreviewResult: onPreviewResultUpdate,
        globalStart: start,
        globalEnd: end,
        setElevatedId: setElevatedId,
        isInteractionLocked: isLocked,
    };

    return (
        <NodeOperationsContext.Provider value={nodeOps}>
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: 'var(--bg-app)' }}>
                <TransformWrapper
                    minScale={0.2}
                    maxScale={2}
                    initialScale={1}
                    initialPositionX={0}
                    initialPositionY={0}
                    limitToBounds={false}
                    centerOnInit={false}
                    centerZoomedOut={false}
                    doubleClick={{ disabled: true }}
                    wheel={{ step: 0.1 }}
                    panning={{ disabled: isInteracting, activationKeys: [] }}
                >
                    {({ resetTransform }) => (
                        <>
                            {/* --- Floating Control Dock --- */}
                            <div className={styles.toolbar}>
                                <div className="btn-group gap-xs">
                                    <button className="btn-icon" onClick={handleUndo} title="Undo" disabled={isLocked}>↩</button>
                                    <button className="btn-icon" onClick={handleRedo} title="Redo" disabled={isLocked}>↪</button>
                                    <button onClick={() => resetTransform()} title="Center / Fit View" className={styles.fitButton}>Fit</button>
                                </div>

                                <div className={styles.divider}></div>

                                {/* JSON Tools */}
                                <div className="btn-group">
                                    <button
                                        className="btn-icon" title="Load JSON"
                                        disabled={isLocked}
                                        onClick={async () => {
                                            try {
                                                const text = await navigator.clipboard.readText();
                                                const parsed = JSON.parse(text);
                                                const deserialized = deserializeNode(parsed.data ?? parsed);
                                                handleUpdateNodes([deserialized]);
                                            } catch (err) {
                                                console.error("Clipboard read failed:", err);
                                                alert("Failed to read clipboard or invalid JSON");
                                            }
                                        }}
                                    >
                                        📥
                                    </button>
                                    <button
                                        className="btn-icon" title="Copy JSON"
                                        disabled={isLocked}
                                        onClick={() => {
                                            if (nodes.length === 0) return;
                                            const serialized = serializeNode(nodes[0], start, end);
                                            navigator.clipboard.writeText(JSON.stringify(serialized, null, 2));
                                        }}
                                    >
                                        📤
                                    </button>
                                </div>

                                <div className={styles.divider}></div>

                                <div className="flex flex-col">
                                    <label className={styles.dateLabel}>Start</label>
                                    <input
                                        type="date"
                                        value={start ?? ''}
                                        onChange={(e) => setStart(e.target.value || null)}
                                        className={styles.dateInput}
                                        disabled={isLocked}
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className={styles.dateLabel}>End</label>
                                    <input
                                        type="date"
                                        value={end ?? ''}
                                        onChange={(e) => setEnd(e.target.value || null)}
                                        className={styles.dateInput}
                                        disabled={isLocked}
                                    />
                                </div>

                                <button
                                    onClick={onQuickEvaluate}
                                    disabled={isLocked}
                                    className={`${styles.runButton} ${isEvaluating ? styles.evaluating : ''}`}
                                >
                                    {isEvaluating ? 'Run...' : '▶ Run'}
                                </button>
                            </div>

                            <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
                                <div
                                    className="grid-background"
                                    onDrop={(e) => {
                                        if (isLocked) {
                                            return;
                                        }
                                        handleNodeDrop(e, (droppedNode) => {
                                            if (nodes.length === 0) {
                                                handleUpdateNodes([droppedNode]);
                                            }
                                        });
                                    }}
                                    onDragOver={(e) => {
                                        if (!isLocked) {
                                            e.preventDefault();
                                        }
                                    }}
                                    style={{
                                        width: `${canvasWidth}px`,
                                        height: `${canvasHeight}px`,
                                        position: 'relative',
                                    }}
                                >
                                    {nodes.length === 0 && (
                                        <div style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem',
                                            opacity: 0.7,
                                            paddingTop: '4rem',
                                            paddingLeft: `${CANVAS_PADDING}px`,
                                            width: 'fit-content'
                                        }}>
                                            <div style={{
                                                fontSize: '3rem',
                                                color: 'var(--text-muted)',
                                                border: '2px dashed var(--text-muted)',
                                                borderRadius: '50%',
                                                width: '120px', height: '120px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}>
                                                ↓
                                            </div>
                                            <h3 style={{ color: 'var(--text-secondary)' }}>Drag components here to start</h3>

                                            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                {starterActions.map((action) => (
                                                    <button
                                                        key={action.id}
                                                        disabled={isLocked}
                                                        onClick={() => handleInsertRootNode(action.create())}
                                                        style={action.borderColor ? { border: `1px solid ${action.borderColor}` } : undefined}
                                                    >
                                                        {action.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* ── SVG connector layer ─────────────────────────── */}
                                    {nodes.length > 0 && (
                                        <TreeConnectors
                                            layoutMap={layoutMap}
                                            nodes={flatNodes}
                                            filterParentIds={logicParentSets.filters}
                                            comparisonParentIds={logicParentSets.comparisons}
                                            nodeMetadata={nodeMetadata} offset={CANVAS_PADDING}
                                        />
                                    )}

                                    {/* ── Flat node cards at absolute positions ────────── */}
                                    {flatNodes.map((node) => {
                                        const layout = layoutMap.get(node.id);
                                        if (!layout) return null;
                                        return (
                                            <div
                                                key={node.id}
                                                style={{
                                                    position: 'absolute',
                                                    left: layout.x + CANVAS_PADDING,
                                                    top: layout.y + CANVAS_PADDING,
                                                    // Let width and height be natural — height/width in layout is just for spacing math
                                                    zIndex: node.id === elevatedId ? 100 : 1,
                                                }}
                                                // Stop pointer events from bleeding into TransformWrapper pan
                                                onPointerDown={(e) => e.stopPropagation()}
                                            >
                                                {node.type === 'Placeholder' ? (
                                                    <PlaceholderCard node={node} />
                                                ) : (
                                                    <StrategyNodeCard
                                                        node={node}
                                                        slot={layout.slot}
                                                        parentId={layout.parentId}
                                                        parentOperator={parentOperatorMap.get(node.id)}
                                                        parentType={parentTypeMap.get(node.id)}
                                                        strategyFamily={strategyFamily}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}

                                    {isLocked && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                background: 'rgba(7, 12, 21, 0.38)',
                                                backdropFilter: 'blur(1px)',
                                                zIndex: 250,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'center',
                                                pointerEvents: 'auto',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    marginTop: '3rem',
                                                    padding: '0.85rem 1rem',
                                                    borderRadius: '12px',
                                                    border: '1px solid var(--surface-glass-border)',
                                                    background: 'rgba(9, 13, 21, 0.92)',
                                                    color: 'var(--text-primary)',
                                                    boxShadow: 'var(--shadow-xl)',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                QuantArc run in progress. Editing is locked.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </TransformComponent>
                        </>
                    )}
                </TransformWrapper>
            </div>
        </NodeOperationsContext.Provider>
    );
}
